import React, { useCallback, useMemo } from "react";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import EntityFormFields from "rs-components/CreateDialog/components/EntityFormFields";
import Dialog from "components/Dialog";
import { GlobalEntityType } from "shared/types";
import { serverErrorsSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { createEntityFromResultElementSource } from "store/rs-workspace/actions/results.actions";
import { EntitiesCreatableFromResultElement, ResultSetElementType, } from "store/rs-workspace/rs-workspace.types";
import { workspaceCellModelsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { DIALOG_ACTION_TITLE } from "shared/labels";
import { getCreateEntityRequestTemplate } from "./utils";
import * as styles from "./styles.module.scss";
const CreateEntityFromResultElementDialog = ({ isOpen, entityType, entityId, resultElementType, elementId, rowId, sourceId, parameterValues, onClose, }) => {
    const dispatch = useDispatch();
    const isCellModel = useMemo(() => entityType === EntitiesCreatableFromResultElement.CellModel, [entityType]);
    const requestTemplate = useMemo(() => getCreateEntityRequestTemplate(resultElementType, entityType), [resultElementType, entityType]);
    const simulations = useSelector(workspaceSimulationsSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const serverErrors = useSelector(serverErrorsSelector(requestTemplate));
    const baseEntitySimulation = useMemo(() => simulations?.find(({ id }) => id === entityId), [entityId, simulations]);
    const baseEntityRecipe = useMemo(() => recipes.find(({ id }) => id === baseEntitySimulation?.recipeId), [baseEntitySimulation?.recipeId, recipes]);
    const baseEntityCellModel = useMemo(() => cellModels.find(({ id }) => id === baseEntitySimulation?.cellModelId), [baseEntitySimulation?.cellModelId, cellModels]);
    const baseEntityData = useMemo(() => (isCellModel ? baseEntityCellModel : baseEntityRecipe), [baseEntityCellModel, baseEntityRecipe, isCellModel]);
    const initialValues = useMemo(() => ({
        name: `Copy of ${baseEntityData?.name}`,
        description: baseEntityData?.description ?? "",
        tags: baseEntityData?.tags?.map((tag) => ({ value: tag?.id?.toString(), label: tag?.name })),
    }), [baseEntityData?.description, baseEntityData?.name, baseEntityData?.tags]);
    const onCreateEntity = useCallback((values) => {
        const data = {
            name: values.name,
            description: values.description,
            tags: values.tags?.map(({ label }) => label),
        };
        const basePayload = {
            resultElementType,
            elementId,
            data,
            entityType,
        };
        switch (resultElementType) {
            case ResultSetElementType.Table:
                dispatch(createEntityFromResultElementSource({
                    ...basePayload,
                    sourceId: rowId ?? 0,
                }));
                break;
            case ResultSetElementType.ProcessDataViewer:
                dispatch(createEntityFromResultElementSource({
                    ...basePayload,
                    sourceId: sourceId ?? 0,
                }));
                break;
            case ResultSetElementType.SpaceViewer:
            case ResultSetElementType.LineGraph:
                dispatch(createEntityFromResultElementSource({
                    ...basePayload,
                    sourceId: sourceId ?? 0,
                    parameterValues,
                }));
                break;
            default:
                break;
        }
    }, [dispatch, elementId, entityType, parameterValues, resultElementType, rowId, sourceId]);
    return (React.createElement(Form, { onSubmit: onCreateEntity, initialValues: initialValues, render: ({ handleSubmit, form, values, invalid }) => (React.createElement(Dialog, { isOpen: isOpen, title: `New ${isCellModel ? "Cell model" : "Recipe"}`, primaryButton: {
                label: DIALOG_ACTION_TITLE.OK,
                onClick: handleSubmit,
                disabled: invalid || !values.name,
            }, secondaryButtons: [{ label: DIALOG_ACTION_TITLE.CANCEL, onClick: onClose }], onClose: async () => await form.reset(), className: styles.dialog },
            React.createElement("div", { className: styles.content },
                React.createElement(EntityFormFields, { serverErrors: serverErrors, entityType: isCellModel ? GlobalEntityType.CellModel : GlobalEntityType.Recipe })))) }));
};
export default CreateEntityFromResultElementDialog;
